import { create } from "zustand";
import { useObjectiveStore } from "./objective.store";

export const useStrategicInitiativeStore = create((set, get) => ({
    data: [],
    loading: false,
    error: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 5,
    },
    ganttData: [],
    searchQuery: "",
    sort: null,
    columnFilters: {},
    baseParams: {},
    uniqueValues: {},

    setData: (data) => set({ data }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setPagination: (pagination) => set((state) => ({ pagination: { ...state.pagination, ...pagination } })),
    setSort: (sort) => set({ sort }),
    setUniqueValues: (uniqueValues) => set({ uniqueValues }),

    fetchData: async (params = {}) => {
        const { strategicInitiativeIntegration } = await import("@/lib/api/client");
        return await strategicInitiativeIntegration.fetchStrategicInitiatives(params);
    },

    fetchRelatedInitiatives: async (objectiveId, params = {}) => {
        if (!objectiveId) return;
        const { objectiveIntegration } = await import("@/lib/api/client");
        return await objectiveIntegration.fetchInitiatives(objectiveId, params);
    },

    linkInitiatives: async (objectiveId, selectedIds) => {
        try {
            const { objectiveIntegration } = await import("@/lib/api/client");
            const response = await objectiveIntegration.addInitiatives(objectiveId, selectedIds);
            if (response.success) {
                await get().fetchRelatedInitiatives(objectiveId);
            }
            return response;
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    unlinkInitiative: async (objectiveId, initiativeId, opts = {}) => {
        try {
            const { objectiveIntegration } = await import("@/lib/api/client");
            const response = await objectiveIntegration.removeInitiative(objectiveId, initiativeId, opts);
            if (response.success) {
                await get().fetchRelatedInitiatives(objectiveId);
            }
            return response;
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    createItem: async (objectiveId, formData) => {
        try {
            const { strategicInitiativeIntegration } = await import("@/lib/api/client");
            const response = await strategicInitiativeIntegration.createStrategicInitiative(formData);
            if (response.success && objectiveId) {
                await get().linkInitiatives(objectiveId, [response.data._id || response.data.id]);
            }
            return response.success;
        } catch (error) {
            return false;
        }
    },

    updateItem: async (objectiveId, initiativeId, formData) => {
        try {
            const { strategicInitiativeIntegration } = await import("@/lib/api/client");
            const response = await strategicInitiativeIntegration.updateStrategicInitiative(initiativeId, formData);
            if (response.success && objectiveId) {
                await get().fetchRelatedInitiatives(objectiveId);
            }
            return response.success;
        } catch (error) {
            return false;
        }
    },

    deleteItem: async (objectiveId, initiativeId) => {
        try {
            const { strategicInitiativeIntegration } = await import("@/lib/api/client");
            const response = await strategicInitiativeIntegration.deleteStrategicInitiative(initiativeId);
            if (response.success && objectiveId) {
                await get().fetchRelatedInitiatives(objectiveId);
            }
            return response.success;
        } catch (error) {
            return false;
        }
    },

    fetchGanttData: async (objectiveId, relatedInitiatives = []) => {
        if (!objectiveId) return;
        set({ loading: true });
        try {
            const { objectiveIntegration } = await import("@/lib/api/client");
            // Use direct service call to prevent overwriting global store pagination with 9999 limit
            const response = await objectiveIntegration.service.getRelatedInitiatives(objectiveId, {
                page: 1, limit: 9999
            });
            // Map createdBy properties to ownerId and name to title so Gantt chart reads it properly if needed, although backend returns StrategicInitiative format directly
            set({ ganttData: response.data || [], loading: false });
        } catch (error) {
            set({ error: error.message, loading: false });
        }
    },

    setSearchQuery: (query) => set({ searchQuery: query }),
    setColumnFilters: (filters) => set({ columnFilters: filters }),
    setBaseParams: (params) => set({ baseParams: params }),
    clearError: () => set({ error: null }),
}));
